import React, { useState, useEffect, forwardRef } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Divider,
  Slide,
  useTheme,
} from "@mui/material";
import { Outlet, useNavigate } from "react-router-dom";
import { TransitionProps } from "@mui/material/transitions";
import AccountCircle from "@mui/icons-material/AccountCircle";
import Logout from "@mui/icons-material/Logout";
import { jwtDecode } from "jwt-decode";
import logo from "../components/images/kineticlogo1.png";
import {
  Close,
} from "@mui/icons-material";
import { Transition } from "../components/assets/assets"

interface DecodedToken {
  name?: string;
  email?: string;
  avatarUrl?: string;
}

const Header: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [user, setUser] = useState<DecodedToken | null>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded: DecodedToken = jwtDecode(token);
        setUser(decoded);
      } catch (error) {
        console.error("Invalid token", error);
      }
    }
  }, []);

  const handleLogoutConfirm = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
    handleMenuClose();
  };

  return (
    <Box>
      <AppBar position="static" style={{ backgroundColor: "red" }}>
        <Toolbar
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          {/* Logo */}

          <Box
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => navigate("/home")}
          >
            <img
              src={logo}
              alt="Logo"
              style={{ width: "40px", height: "40px", borderRadius: "50%" }}
            />
            <Typography variant="h6" sx={{ color: "white", marginLeft: 1 }}>
              Kinetic
            </Typography>
          </Box>

          {/* Profile Dropdown */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton onClick={handleMenuOpen} color="inherit">
              <Avatar sx={{ width: 32, height: 32, bgcolor: "white", color: "black" }}>
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.name}
                    style={{ width: "100%", height: "100%" }}
                  />
                ) : user?.name ? (
                  user.name.charAt(0).toUpperCase()
                ) : (
                  <AccountCircle />
                )}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                elevation: 3,
                sx: {
                  minWidth: 220,
                  padding: 1,
                  borderRadius: 2,
                },
              }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem>
                <Box display="flex" flexDirection="column">
                  <Typography
                    variant="subtitle1"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate('/profile')}
                  >
                    {user?.name || 'Guest'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.email}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogoutClick}>
                <Logout fontSize="small" sx={{ marginRight: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ height: "100vh", background: "#EEEEEE" }}>
        <Outlet />
      </Box>

      {/*Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: 90,
            background: theme.palette.background.paper,
            transition: 'transform 0.3s ease',
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(5px)',
            WebkitBackdropFilter: 'blur(5px)',
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "linear-gradient(144deg, rgba(252, 133, 69, 1) 0%, rgba(253, 29, 29, 1) 50%, rgba(252, 133, 69, 0.87) 100%)",
            px: 2,
            py: 1,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
        >
          <DialogTitle sx={{ color: "white", p: 0, fontWeight: 'bold', fontSize: '1.5rem' }}>Log Out</DialogTitle>
          <IconButton onClick={() => setLogoutDialogOpen(false)} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent sx={{ textAlign: "center", py: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem', color: theme.palette.text.primary }}>
            Are you sure you want to log out?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pt: 2 }}>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            sx={{
              background: "linear-gradient(144deg, rgba(252, 133, 69, 1) 0%, rgba(253, 29, 29, 1) 50%, rgba(252, 133, 69, 0.87) 100%)",
              '&:hover': {
                background: "linear-gradient(144deg, rgba(253, 29, 29, 1) 0%, rgba(252, 133, 69, 1) 50%, rgba(253, 29, 29, 0.87) 100%)", // Hover effect
              },
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Header;
